/**
 * Centralized API error handling: single source of truth for the
 * user-facing messages shown when a request fails.
 */

export const DEFAULT_ERROR_MESSAGE =
  "The Operation could not be completed. Please try again";

/**
 * Generic messages per HTTP status. Used when the backend does not
 * provide a specific message (or the status does not expose it).
 */
export const HTTP_ERROR_MESSAGES = Object.freeze({
  400: "The request is invalid. Please review the data and try again",
  403: "You don't have permission to perform this action",
  404: "The requested resource was not found",
  409: "The operation conflicts with existing records",
  422: "The data could not be processed. Please review it and try again",
  429: "Too many requests. Please wait a moment and try again",
  500: "Server error. Please try again later",
  502: "Service temporarily unavailable. Please try again later",
  503: "Service temporarily unavailable. Please try again later",
  504: "The server took too long to respond. Please try again"
});

/**
 * Statuses whose backend message is meant for the user
 * (e.g. 409 deletion blocked by dependent products or active reservations).
 */
const BACKEND_MESSAGE_STATUSES = [400, 409, 422];

/**
 * Generic backend messages that don't tell the user what failed
 * (e.g. Joi validation responses send "bad request").
 */
const GENERIC_BACKEND_MESSAGES = ["bad request"];

/**
 * Check if the error requires signing out (401).
 * @param {Object} error - Axios error.
 * @returns {boolean}
 */
export function isUnauthorizedError(error) {
  const { response } = error || {};
  return !!response && (response.status === 401 || (response.data || {}).code === 401);
}

/**
 * Resolve the message to show for a failed request.
 * @param {Object} error - Axios error.
 * @param {string} [fallback] - Message used for network errors or unmapped statuses.
 * @returns {string}
 */
export function getErrorMessage(error, fallback = DEFAULT_ERROR_MESSAGE) {
  const { response } = error || {};
  if (!response) return fallback;

  const { status, data = {} } = response;
  const isGenericMessage = GENERIC_BACKEND_MESSAGES.includes(
    String(data.message || "").toLowerCase()
  );
  const isBackendMessageStatus = BACKEND_MESSAGE_STATUSES.includes(status);
  if (isBackendMessageStatus && data.message && !isGenericMessage) {
    return data.message;
  }
  return HTTP_ERROR_MESSAGES[status] || fallback;
}

/**
 * Show a danger notification.
 * @param {Object} vm - Vue component instance.
 * @param {string} message
 */
export function notifyError(vm, message) {
  vm.$notify({
    type: "danger",
    title: "Error!",
    message,
    icon: "flaticon-remove"
  });
}

/**
 * Handle a failed API request: signs out on 401, otherwise notifies the user.
 * Requires the component to map the `setSigOut` mutation.
 * @param {Object} vm - Vue component instance.
 * @param {Object} error - Axios error.
 * @param {string} [fallback] - Message used for network errors or unmapped statuses.
 */
export function handleApiError(vm, error, fallback) {
  if (isUnauthorizedError(error)) {
    vm.setSigOut(true);
    return;
  }
  notifyError(vm, getErrorMessage(error, fallback));
}
